import { useDatasets } from 'api/dataset/dataset.api'
import {
  useCreateElplanMutation,
  useElplan,
  useElplanBids,
  useElplanInfo,
  useElplanSyncResultsMutation,
  useToggleLockMutation,
  useUpdateElplanMutation,
} from 'api/elplan/elplan.api'
import { resolveElplanBidsQueryEndTime } from 'api/elplan/elplan.bidQueryWindow.helper'
import { ELPLAN_BID_REFRESH_EVENT_QUERY_KEY, ELPLAN_REFRESH_EVENT_QUERY_KEY } from 'api/elplan/elplan.constants'
import { PlanValues } from 'api/elplan/elplan.types'
import { useUiConfigAnchorComponent } from 'api/uiConfig/uiConfig.api'
import { getCalculatedDataset } from 'helpers/dataset.helper/dataset.calc.helper'
import { convertNumberDecimalSign } from 'helpers/global.helper/global.helper'
import { queryClient } from 'helpers/queryClient'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import elplanStore from 'store/elplan/elplan'
import { Button, Card } from 'ui/atoms'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import UiConfig from 'ui/uiConfig/UiConfig'
import Datetime from 'utils/datetime/datetime'
import { useSnapshot } from 'valtio'
import { getDatasetInstructionsForSumVolumeCalc } from '../DayAheadBidManagement/DayAheadBidManagement.helper'
import { DatepickerInFormWithSwitch } from '../shared/DatepickerInFormWithSwitch'
import styles from './DayAheadPlan.module.less'
import EditElectricityPlanModal from './EditElectricityPlanModal/EditElectricityPlanModal'

const QUARTERS_PER_DAY = 96

function getElectricityPlan(dataset: Dataset): PlanValues[] {
  const array: PlanValues[] = []
  const planIsQuarterlyResolution = dataset.times.length % QUARTERS_PER_DAY === 0

  dataset.times.forEach((time, index) => {
    const isoDatetime = time as ISODateTime
    const isLastItem = dataset.times.length - 1 === index
    array.push({
      volume: dataset.values[index] ?? 0,
      start_time: isoDatetime,
      end_time: isLastItem
        ? planIsQuarterlyResolution
          ? new Datetime(isoDatetime).addMinutes(15).ISOString
          : Datetime.getEndOfDay(isoDatetime)
        : (dataset.times[index + 1] as ISODateTime),
      time: isoDatetime,
    })
  })

  return array
}

export default function DayAheadPlan(): ReactElement {
  const { t } = useTranslation()
  const auth = useAuth()
  const elplanInfo = useElplanInfo(auth.systemId)

  const elplanSnap = useSnapshot(elplanStore)

  const [modalIsOpen, setModalIsOpen] = useState<boolean>()
  const [isEditingPlan, setIsEditingPlan] = useState<boolean>(false)
  const [confirmDialog, setConfirmDialog] = useState<boolean>(false)

  useElplan(
    elplanSnap.elplan_info_id,
    elplanSnap.date.start_time
      ? (Datetime.toLocalTime(elplanSnap.date.start_time, 'onlyDate') as ISODateTime)
      : undefined
  )

  const amountOfBlockBids = elplanSnap.blockBidInfo.amount || 0
  const onlyReturnIds = new Array(amountOfBlockBids).fill('').map((_, i) => `blockbid.${i + 1}.volume`)

  const bidsQueryEndDate = elplanSnap.date.end_time && resolveElplanBidsQueryEndTime(elplanSnap.date.end_time)
  const { data: elplanBidsExistForCurrentDate } = useElplanBids(
    elplanSnap.elplan_info_id,
    elplanSnap.date.start_time,
    bidsQueryEndDate
  )
  const { data: elplanBidsUiConfig } = useUiConfigAnchorComponent('elplan_bids')
  const { data: elplanBidsDatasets = [] } = useDatasets(
    elplanBidsUiConfig?.id ?? -1,
    elplanBidsUiConfig?.version ?? -1,
    elplanBidsUiConfig?.dataset_instructions || [],
    elplanSnap.date.start_time,
    bidsQueryEndDate,
    {
      onlyReturnIds,
      fillMissingHourTimestamps: false,
      ensureUniformTimeIntervals: false,
      datasetRefreshToken: ELPLAN_BID_REFRESH_EVENT_QUERY_KEY,
      overrideAlias: {
        elplan_info_id: elplanSnap.elplan_info_id,
      },
    }
  )

  const electricityPlanFromBids = useMemo(() => {
    if (!elplanBidsDatasets?.length) {
      return []
    }

    const calcDatasets = getCalculatedDataset(
      elplanBidsDatasets,
      getDatasetInstructionsForSumVolumeCalc(amountOfBlockBids)
    )
    return getElectricityPlan(calcDatasets)
  }, [elplanBidsDatasets, amountOfBlockBids])

  const elplanExistsForCurrentDate = elplanSnap.elplan.has_plan
  const isLocked = elplanSnap.elplan.is_locked
  const elplanId = elplanSnap.elplan.elplan_id

  const { mutate: createElplanValues } = useCreateElplanMutation()
  const { mutate: updateElplanValues } = useUpdateElplanMutation()
  const { mutate: toggleLockVerification } = useToggleLockMutation()
  const { mutate: syncElplanResults } = useElplanSyncResultsMutation()

  // Invalidate queries on mount to ensure fresh data when component initializes
  // Child hooks (useElplanBids, useDatasets) handle their own refetching when date changes
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: [ELPLAN_REFRESH_EVENT_QUERY_KEY] })
  }, [])

  function updatingPlan(elplan: PlanValues[]): void {
    const values = elplan.map((value) => {
      return {
        id: value.id as number,
        volume: convertNumberDecimalSign(value.volume),
      }
    })
    updateElplanValues({ id: elplanId, values })
  }

  function onCreatingPlan(): void {
    setModalIsOpen(true)
    setIsEditingPlan(!isEditingPlan)
  }

  function onEditingPlan(): void {
    setModalIsOpen(true)
    setIsEditingPlan(!isEditingPlan)
  }

  function createPlan(electricityPlan: PlanValues[]): void {
    const values = electricityPlan.map((bid) => {
      return {
        time: bid.time,
        volume: convertNumberDecimalSign(bid.volume),
      }
    })
    setModalIsOpen(false)

    if (elplanSnap.date.start_time) {
      createElplanValues({
        elplan_info: elplanSnap.elplan_info_id,
        date: Datetime.toLocalTime(elplanSnap.date.start_time, 'onlyDate'),
        values,
      })
    }
  }

  const noIntegration =
    elplanInfo?.data?.integration === undefined ||
    elplanInfo?.data?.integration === null ||
    elplanInfo?.data?.integration === ''

  const onClickToggleLock = elplanSnap.elplan.is_locked
    ? () => toggleLockVerification({ id: elplanId })
    : () => setConfirmDialog(true)

  const onConfirmLockClick = () => {
    setConfirmDialog(false)
    toggleLockVerification({ id: elplanId })
  }

  return (
    <>
      <Card title={t(`Day ahead plan`)}>
        <div className={styles.DayAheadPlan_Period}>
          <DatepickerInFormWithSwitch defaultDay={'tomorrow'} />
          {elplanExistsForCurrentDate && (
            <InfoBanner
              text={
                elplanSnap.elplan.is_locked
                  ? t('The electricity plan is locked in production plan')
                  : t(
                      'As of now, this electricity plan does not affect the production plan. Lock the electricity plan with the button on the right'
                    )
              }
              style={!elplanSnap.elplan.is_locked ? 'warning' : 'info'}
              variant={!elplanSnap.elplan.is_locked ? 'textWithBackground' : 'text'}
            />
          )}
        </div>

        <div className={styles.DayAheadPlan_Buttons__right}>
          {!noIntegration && !isLocked && (
            <Button
              secondary
              marginBottom
              icon={'fal fa-sync'}
              onClick={() => {
                if (!auth.activeSystem?.name || !elplanSnap.date.start_time) {
                  return
                }

                syncElplanResults({
                  date: Datetime.toLocalTime(elplanSnap.date.start_time, 'onlyDate'),
                  system: auth.activeSystem?.name,
                  systemId: auth.activeSystem.id,
                })
              }}
            >
              {t('Fetch results')}
            </Button>
          )}
          {!elplanExistsForCurrentDate ? (
            <Button
              primary
              icon={'fal fa-plus'}
              marginLeft
              marginBottom
              disabled={!(elplanBidsExistForCurrentDate && elplanBidsExistForCurrentDate.length > 0) ? true : false}
              onClick={() => onCreatingPlan()}
            >
              {t('Create electricity plan from bids')}
            </Button>
          ) : elplanExistsForCurrentDate && !elplanSnap.elplan.is_locked ? (
            <Button
              icon={'fal fa-pen'}
              primary
              marginBottom
              marginLeft
              disabled={elplanSnap.elplan.is_locked}
              onClick={() => onEditingPlan()}
            >
              {t('Edit electricity plan')}
            </Button>
          ) : null}
          {elplanExistsForCurrentDate ? (
            <Button
              icon={elplanSnap.elplan.is_locked ? 'fal fa-unlock' : 'fal fa-lock'}
              primary={!elplanSnap.elplan.is_locked}
              warning={elplanSnap.elplan.is_locked}
              disabled={!elplanExistsForCurrentDate}
              marginLeft
              marginBottom
              tooltip={
                elplanSnap.elplan.is_locked ? t('Electricitiy plan will no longer be locked in production plan') : ''
              }
              onClick={onClickToggleLock}
            >
              {elplanSnap.elplan.is_locked ? t('Unlock to edit') : t('Lock in production plan')}
            </Button>
          ) : null}
        </div>

        <div className={styles.DayAheadPlan_Tables}>
          <UiConfig
            type="day_ahead_overview"
            datasetStartTime={elplanSnap.date.start_time}
            datasetEndTime={elplanSnap.date.end_time}
            datasetRefreshToken={ELPLAN_REFRESH_EVENT_QUERY_KEY}
          />
        </div>
      </Card>

      {modalIsOpen && (
        <EditElectricityPlanModal
          onClose={() => setModalIsOpen(false)}
          onSubmit={(electricityPlan) =>
            elplanExistsForCurrentDate ? updatingPlan(electricityPlan) : createPlan(electricityPlan)
          }
          elplanExistsWarningText={t(
            'You are currently editing an already created electricity plan. Any changes you make to this plan may differ from the plan you have sent into trading.'
          )}
          planValues={!elplanExistsForCurrentDate ? electricityPlanFromBids : elplanSnap.elplan.values}
        />
      )}
      {confirmDialog && (
        <ConfirmDialog
          open={confirmDialog}
          onConfirm={onConfirmLockClick}
          onClose={() => setConfirmDialog(false)}
          textObject={{
            title: t(`Are you sure you want to lock a manual electric plan?`),
            text: t(
              `Note that if you lock a manual electricity plan, the automatic electricity plan will not automatically enter. Do you want to continue to lock the electricity plan?`
            ),
          }}
        />
      )}
    </>
  )
}
